import { useEffect, useRef, useCallback } from 'react';
import { api } from '../api/client';

/**
 * Global Shopify auto-sync hook.
 * Runs in Layout (always mounted) so sync continues even when user navigates away.
 * Checks every 5 minutes if auto-sync is enabled, then processes batches.
 * When articles are pending: 30s interval. When all synced: 5min interval.
 */

interface AutoSyncState {
  lastSyncTime: string | null;
  nextSyncTime: string | null;
  remaining: number;
  lastResult: { success: number; error: number; errors?: Array<{ articleId: string; error: string }> } | null;
  active: boolean;
}

// Shared state accessible from other components (ShopifyIntegration page)
const state: AutoSyncState = {
  lastSyncTime: null,
  nextSyncTime: null,
  remaining: 0,
  lastResult: null,
  active: false,
};

// Immutable snapshot for useSyncExternalStore (must be referentially stable)
let snapshot: AutoSyncState = { ...state };

// Listeners for state changes
type Listener = () => void;
const listeners = new Set<Listener>();

function notifyListeners() {
  // Create new snapshot only when state changes
  snapshot = { ...state };
  listeners.forEach(fn => fn());
}

export function getAutoSyncState(): AutoSyncState {
  return snapshot;
}

export function subscribeAutoSync(listener: Listener): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

// Manual trigger (callable from ShopifyIntegration page)
let manualTriggerFn: (() => Promise<AutoSyncState>) | null = null;

export function triggerManualSync(): Promise<AutoSyncState> | null {
  return manualTriggerFn ? manualTriggerFn() : null;
}

export function useShopifyAutoSync() {
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const runningRef = useRef(false);
  const enabledRef = useRef(false);
  const checkTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const runBatch = useCallback(async (): Promise<{ success: number; error: number; remaining: number; errors?: Array<{ articleId: string; error: string }> }> => {
    if (runningRef.current) return { success: 0, error: 0, remaining: state.remaining };
    runningRef.current = true;
    try {
      const result = await api.post<{ success: number; error: number; remaining: number; errors?: Array<{ articleId: string; error: string }>; message?: string }>('/shopify/products/auto-sync', { batchSize: 10 });
      const synced = result?.success ?? 0;
      const errorCount = result?.error ?? 0;
      const remaining = result?.remaining ?? 0;

      state.remaining = remaining;
      state.lastResult = { success: synced, error: errorCount, errors: result?.errors };
      state.lastSyncTime = new Date().toISOString();
      notifyListeners();

      return { success: synced, error: errorCount, remaining, errors: result?.errors };
    } catch {
      return { success: 0, error: 0, remaining: 0 };
    } finally {
      runningRef.current = false;
    }
  }, []);

  const scheduleNext = useCallback((remaining: number) => {
    if (!enabledRef.current) return;
    if (timerRef.current) clearTimeout(timerRef.current);

    const interval = remaining > 0 ? 30_000 : 5 * 60_000;
    state.nextSyncTime = new Date(Date.now() + interval).toISOString();
    notifyListeners();

    timerRef.current = setTimeout(async () => {
      if (!enabledRef.current) return;
      const result = await runBatch();
      scheduleNext(result.remaining);
    }, interval);
  }, [runBatch]);

  const startAutoSync = useCallback(() => {
    if (enabledRef.current) return; // already running
    enabledRef.current = true;
    state.active = true;
    notifyListeners();
    runBatch().then(result => scheduleNext(result.remaining));
  }, [runBatch, scheduleNext]);

  const stopAutoSync = useCallback(() => {
    enabledRef.current = false;
    state.active = false;
    state.nextSyncTime = null;
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    notifyListeners();
  }, []);

  // Check if auto-sync should be enabled (polls connection status)
  const checkAutoSyncEnabled = useCallback(async () => {
    try {
      const result = await api.getShopifyConnection();
      const conn = result?.connection;
      const shouldRun = result?.isConnected && conn?.syncProducts && conn?.autoSync;

      if (shouldRun && !enabledRef.current) {
        if (conn?.lastProductSync) {
          state.lastSyncTime = conn.lastProductSync;
        }
        startAutoSync();
      } else if (!shouldRun && enabledRef.current) {
        stopAutoSync();
      }
    } catch {
      // No connection or error - don't start
    }
  }, [startAutoSync, stopAutoSync]);

  // Register manual trigger
  useEffect(() => {
    manualTriggerFn = async () => {
      const result = await runBatch();
      // If auto-sync is active, reschedule after manual trigger
      if (enabledRef.current) {
        scheduleNext(result.remaining);
      }
      return getAutoSyncState();
    };
    return () => { manualTriggerFn = null; };
  }, [runBatch, scheduleNext]);

  // On mount: check once, then re-check every 5 minutes
  useEffect(() => {
    checkAutoSyncEnabled();
    checkTimerRef.current = setInterval(checkAutoSyncEnabled, 5 * 60_000);

    return () => {
      if (checkTimerRef.current) clearInterval(checkTimerRef.current);
      if (timerRef.current) clearTimeout(timerRef.current);
      enabledRef.current = false;
      state.active = false;
    };
  }, [checkAutoSyncEnabled]);
}
